/* eslint-disable prettier/prettier */
import React, { useMemo, useState } from "react";
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";
import styles from "./EdificiosTable.module.scss";
import "../../assets/stylesChart.scss";

const MESES_LABEL = ["ENE", "FEB", "MAR", "ABR", "MAY", "JUN", "JUL", "AGO", "SEP", "OCT", "NOV", "DIC"];

const COLORS = {
  edificios: "#dc3545",
  personas: "#0c0c58",
  pantallas: "#27ae60",
};

/* ── Tooltip ── */
const CustomTooltip = ({ active, payload, label, series }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload || {};

  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-title">{label}</p>
      {series.includes("edificios") && (
        <p className="chart-tooltip-row" style={{ color: COLORS.edificios }}>
          <span className="chart-tooltip-dot" style={{ background: COLORS.edificios }} />
          Edificios: {d.edificios ?? 0}
        </p>
      )}
      {series.includes("personas") && (
        <p className="chart-tooltip-row" style={{ color: COLORS.personas }}>
          <span className="chart-tooltip-dot" style={{ background: COLORS.personas }} />
          Personas: {(d.personas ?? 0).toLocaleString()}
        </p>
      )}
      {series.includes("pantallas") && (
        <p className="chart-tooltip-row" style={{ color: COLORS.pantallas }}>
          <span className="chart-tooltip-dot" style={{ background: COLORS.pantallas }} />
          Pantallas: {d.pantallas ?? 0}
        </p>
      )}
    </div>
  );
};

/* ── Generar rango de meses entre dos fechas ── */
const generateMonthRange = (startYear, startMonth, endYear, endMonth) => {
  const months = [];
  let y = startYear;
  let m = startMonth;
  while (y < endYear || (y === endYear && m <= endMonth)) {
    months.push({ year: y, month: m });
    m++;
    if (m > 11) {
      m = 0;
      y++;
    }
  }
  return months;
};

/* ── Modal ── */
export const EdificiosChartModal = ({ open, onClose, edificios }) => {
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth();

  // Rango de años disponibles (2023 hasta año actual)
  const aniosDisponibles = useMemo(() => {
    const anios = [];
    for (let y = 2023; y <= currentYear; y++) anios.push(y);
    return anios;
  }, [currentYear]);

  // Estado del rango seleccionado
  const [rangeStart, setRangeStart] = useState({ year: 2023, month: 0 }); // ENE 2023
  const [rangeEnd, setRangeEnd] = useState({ year: currentYear, month: currentMonth });

  // Series visibles
  const [visibleSeries, setVisibleSeries] = useState(["edificios", "personas", "pantallas"]);

  const toggleSeries = (key) => {
    setVisibleSeries((prev) =>
      prev.includes(key) ? prev.filter((s) => s !== key) : [...prev, key]
    );
  };

  // Calcular datos acumulados por mes
  const chartData = useMemo(() => {
    const monthRange = generateMonthRange(rangeStart.year, rangeStart.month, rangeEnd.year, rangeEnd.month);

    return monthRange.map(({ year, month }) => {
      // Fecha de corte: último día del mes
      const corte = new Date(year, month + 1, 0);

      // Contar edificios con inicio_servicio <= corte
      let edCount = 0;
      let personasSum = 0;
      let pantallasSum = 0;

      edificios.forEach((ed) => {
        if (!ed.inicio_servicio) return;
        const inicio = new Date(ed.inicio_servicio + "T12:00:00");
        if (inicio <= corte) {
          edCount++;
          personasSum += ed.personas || 0;
          pantallasSum += ed.pantallas || 0;
        }
      });

      const label = `${MESES_LABEL[month]} ${String(year).slice(2)}`;
      return {
        label,
        edificios: edCount,
        personas: personasSum,
        pantallas: pantallasSum,
      };
    });
  }, [edificios, rangeStart, rangeEnd]);

  // Determinar si necesitamos doble eje Y (personas es mucho mayor que edificios/pantallas)
  const needsDualAxis = useMemo(() => {
    if (!visibleSeries.includes("personas")) return false;
    const maxPersonas = Math.max(...chartData.map((d) => d.personas));
    const maxOther = Math.max(
      ...chartData.map((d) => Math.max(d.edificios, d.pantallas))
    );
    return maxPersonas > maxOther * 5;
  }, [chartData, visibleSeries]);

  // "Hoy" marker
  const hoyLabel = `${MESES_LABEL[currentMonth]} ${String(currentYear).slice(2)}`;

  if (!open) return null;

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
        style={{ width: "90vw", maxWidth: 1400 }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <strong style={{ fontSize: "1rem", color: "#333" }}>Crecimiento Red</strong>

            {/* Toggles de series */}
            <div className="chart-controls" style={{ padding: 0 }}>
              {[
                { key: "edificios", label: "Edificios", color: COLORS.edificios },
                { key: "personas", label: "Personas", color: COLORS.personas },
                { key: "pantallas", label: "Pantallas", color: COLORS.pantallas },
              ].map(({ key, label, color }) => (
                <button
                  key={key}
                  className={`chart-pill ${visibleSeries.includes(key) ? "active" : ""}`}
                  style={visibleSeries.includes(key) ? { background: color, borderColor: color } : {}}
                  onClick={() => toggleSeries(key)}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              fontSize: "1.6rem",
              cursor: "pointer",
              color: "#999",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            &times;
          </button>
        </div>

        {/* Selector de rango de fechas */}
        <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 12, fontSize: "0.82rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#666" }}>Desde:</span>
            <select
              value={rangeStart.month}
              onChange={(e) => setRangeStart((prev) => ({ ...prev, month: parseInt(e.target.value) }))}
              style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: "0.82rem" }}
            >
              {MESES_LABEL.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
            <select
              value={rangeStart.year}
              onChange={(e) => setRangeStart((prev) => ({ ...prev, year: parseInt(e.target.value) }))}
              style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: "0.82rem" }}
            >
              {aniosDisponibles.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ color: "#666" }}>Hasta:</span>
            <select
              value={rangeEnd.month}
              onChange={(e) => setRangeEnd((prev) => ({ ...prev, month: parseInt(e.target.value) }))}
              style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: "0.82rem" }}
            >
              {MESES_LABEL.map((m, i) => (
                <option key={i} value={i}>{m}</option>
              ))}
            </select>
            <select
              value={rangeEnd.year}
              onChange={(e) => setRangeEnd((prev) => ({ ...prev, year: parseInt(e.target.value) }))}
              style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #ccc", fontSize: "0.82rem" }}
            >
              {aniosDisponibles.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Gráfico */}
        <div style={{ width: "100%", height: "60vh", minHeight: 400 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 20, right: needsDualAxis ? 60 : 20, left: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#666" }}
                axisLine={{ stroke: "#ddd" }}
                tickLine={false}
                interval={chartData.length > 24 ? 2 : chartData.length > 12 ? 1 : 0}
              />

              {/* Eje izquierdo: edificios + pantallas */}
              <YAxis
                yAxisId="left"
                tick={{ fontSize: 11, fill: "#999" }}
                axisLine={false}
                tickLine={false}
                width={45}
              />

              {/* Eje derecho: personas (si dual axis) */}
              {needsDualAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 11, fill: "#0c0c58" }}
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}K` : v)}
                  width={50}
                />
              )}

              <Tooltip
                content={<CustomTooltip series={visibleSeries} />}
                cursor={{ fill: "rgba(0,0,0,0.04)" }}
              />

              {/* Marker "Hoy" */}
              <ReferenceLine
                x={hoyLabel}
                yAxisId="left"
                stroke="#c57b1a"
                strokeDasharray="4 4"
                strokeWidth={1.5}
                label={{
                  value: "Hoy",
                  position: "insideTopRight",
                  fill: "#c57b1a",
                  fontSize: 11,
                  fontWeight: 600,
                }}
              />

              {/* Líneas */}
              {visibleSeries.includes("edificios") && (
                <Line
                  yAxisId="left"
                  dataKey="edificios"
                  stroke={COLORS.edificios}
                  strokeWidth={2.5}
                  dot={{ r: chartData.length > 24 ? 0 : 3, fill: COLORS.edificios }}
                  activeDot={{ r: 5 }}
                  type="monotone"
                  name="Edificios"
                />
              )}
              {visibleSeries.includes("pantallas") && (
                <Line
                  yAxisId="left"
                  dataKey="pantallas"
                  stroke={COLORS.pantallas}
                  strokeWidth={2.5}
                  dot={{ r: chartData.length > 24 ? 0 : 3, fill: COLORS.pantallas }}
                  activeDot={{ r: 5 }}
                  type="monotone"
                  name="Pantallas"
                />
              )}
              {visibleSeries.includes("personas") && (
                <Line
                  yAxisId={needsDualAxis ? "right" : "left"}
                  dataKey="personas"
                  stroke={COLORS.personas}
                  strokeWidth={2.5}
                  dot={{ r: chartData.length > 24 ? 0 : 3, fill: COLORS.personas }}
                  activeDot={{ r: 5 }}
                  type="monotone"
                  name="Personas"
                />
              )}
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Leyenda */}
        <div className="chart-legend" style={{ marginTop: 8 }}>
          {visibleSeries.includes("edificios") && (
            <div className="chart-legend-item">
              <span className="chart-legend-line" style={{ borderColor: COLORS.edificios, borderStyle: "solid" }} />
              Edificios
            </div>
          )}
          {visibleSeries.includes("personas") && (
            <div className="chart-legend-item">
              <span className="chart-legend-line" style={{ borderColor: COLORS.personas, borderStyle: "solid" }} />
              Personas {needsDualAxis ? "(eje derecho)" : ""}
            </div>
          )}
          {visibleSeries.includes("pantallas") && (
            <div className="chart-legend-item">
              <span className="chart-legend-line" style={{ borderColor: COLORS.pantallas, borderStyle: "solid" }} />
              Pantallas
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
